/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useEffect, useCallback } from 'react';
import { ConsentStatus, ConsentData } from '../types/consent.types';
import { getConsent, saveConsent, clearAllClientStorage } from '../services/consentStorage';
import { UserAuthManager } from '@/context/authentication';
import { CDU_LOGIN_REQUIRED_EVENT, triggerLoginResumeEvent } from '../utils/consentEvents';
import { migrateInMemoryToLocalStorage } from '@/shared/utils/storage/localStorageWrapper';
import { migrateInMemoryToSessionStorage } from '@/shared/utils/storage/sessionStorageWrapper';
import { migrateInMemoryToRealCookies } from '@/shared/utils/storage/cookieWrapper';
import { migrateInMemoryToRealCookieStore } from '@/shared/utils/storage/cookieStoreWrapper';

/**
 * Migrates all in-memory storage (localStorage, sessionStorage, cookies) to persistent storage.
 */
async function migrateAllStorage(): Promise<void> {
  migrateInMemoryToLocalStorage();
  migrateInMemoryToSessionStorage();
  migrateInMemoryToRealCookies();
  await migrateInMemoryToRealCookieStore();
}

/**
 * Creates a ConsentData object with the given status and current timestamp.
 * @param status - The consent status to record
 * @returns A ConsentData object with version and timestamp
 */
function buildConsentData(status: ConsentStatus): ConsentData {
  return { status, timestamp: Date.now(), version: '1.0' };
}

/**
 * Handles consent rejection by signing out the user and clearing persistent storage.
 */
async function performReject(): Promise<void> {
  try {
    await UserAuthManager.removeUser();
  } catch (error) {
    console.warn('Failed to remove user on consent rejection:', error);
  }

  await saveConsent(buildConsentData('rejected'));
  clearAllClientStorage();
}

/**
 * Reads initial consent status from stored consent data.
 * @returns The stored consent status, or 'pending' if none exists
 */
function getInitialConsentStatus(): ConsentStatus {
  return getConsent()?.status || 'pending';
}

/**
 * Handles consent acceptance by migrating storage and resuming login if needed.
 * @param wasShowingLoginBanner - Whether the login banner was visible at time of acceptance
 */
async function performAccept(wasShowingLoginBanner: boolean): Promise<void> {
  await migrateAllStorage();
  await saveConsent(buildConsentData('accepted'));

  if (wasShowingLoginBanner) {
    triggerLoginResumeEvent();
  }
}

/**
 * Return type for useCduConsent hook.
 */
export interface UseCduConsentReturn {
  /**
   * Current consent status
   */
  consentStatus: ConsentStatus;
  /**
   * Accepts CDU terms and saves consent
   */
  acceptConsent: () => Promise<void>;
  /**
   * Rejects CDU terms and saves rejection
   */
  rejectConsent: () => Promise<void>;
  /**
   * Returns true if user has given consent
   */
  hasConsent: boolean;
  /**
   * Flag indicating if login was attempted with rejected consent
   */
  showBannerForLogin: boolean;
  /**
   * Trigger to show banner when login is attempted with rejected consent
   */
  triggerLoginBanner: () => void;
  /**
   * Clear the login banner trigger
   */
  clearLoginBanner: () => void;
}

/**
 * Hook for managing CDU consent state.
 * Loads consent from storage on mount and provides methods to accept/reject.
 *
 * @returns {UseCduConsentReturn} Consent state and methods
 */
export function useCduConsent(): UseCduConsentReturn {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>(getInitialConsentStatus);
  const [showBannerForLogin, setShowBannerForLogin] = useState(false);

  useEffect(() => {
    setConsentStatus(getInitialConsentStatus());
  }, []);

  useEffect(() => {
    const handleLoginRequired = () => {
      if (consentStatus === 'rejected') {
        setShowBannerForLogin(true);
      }
    };

    window.addEventListener(CDU_LOGIN_REQUIRED_EVENT, handleLoginRequired);
    return () => {
      window.removeEventListener(CDU_LOGIN_REQUIRED_EVENT, handleLoginRequired);
    };
  }, [consentStatus]);

  const acceptConsent = useCallback(async () => {
    await performAccept(showBannerForLogin);
    setConsentStatus('accepted');
    setShowBannerForLogin(false);
  }, [showBannerForLogin]);

  const rejectConsent = useCallback(async () => {
    await performReject();
    setConsentStatus('rejected');
    setShowBannerForLogin(false);
  }, []);

  const triggerLoginBanner = useCallback(() => {
    if (consentStatus === 'rejected') {
      setShowBannerForLogin(true);
    }
  }, [consentStatus]);

  const clearLoginBanner = useCallback(() => {
    setShowBannerForLogin(false);
  }, []);

  const hasConsent = consentStatus === 'accepted';

  return {
    consentStatus,
    acceptConsent,
    rejectConsent,
    hasConsent,
    showBannerForLogin,
    triggerLoginBanner,
    clearLoginBanner,
  };
}
